import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./teamControl.css";
import MobileNav from "../../components/mobileNav/MobileNav";

const TeamControl = () => {
  const [formData, setFormData] = useState({
    id: null,
    title: "",
    imageUrl: "",
  });
  const [errors, setErrors] = useState({
    title: "",
    imageUrl: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const fileInputRef = React.useRef(null);

  // Fetch teams data on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await axios.get(
          "https://api.generalhome.sa/api/Themes/GetTeams"
        );
        setTeams(response.data);
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    fetchTeams();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear the error for the current field when the user types
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { title: "", imageUrl: "" };

    // Validate Title
    if (!formData.title.trim()) {
      newErrors.title = "اسم الفني مطلوب";
      isValid = false;
    }

    // Validate Image URL
    if (!formData.imageUrl) {
      newErrors.imageUrl = "رابط الصورة مطلوب";
      isValid = false;
    }

    // Update errors state
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      let uploadedImageUrl = "";

      // لو المستخدم رفع صورة جديدة (File)
      if (formData.imageUrl instanceof File) {
        const imageFormData = new FormData();
        imageFormData.append("file", formData.imageUrl);

        const uploadResponse = await axios.post(
          "https://api.generalhome.sa/api/Themes/UploadImages",
          imageFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const imageUrl = uploadResponse.data?.imageUrl || uploadResponse.data;

        uploadedImageUrl = imageUrl.startsWith("http")
          ? imageUrl
          : `https://api.generalhome.sa${
              imageUrl.startsWith("/") ? "" : "/"
            }${imageUrl}`;
      } else {
        // لو فيه رابط قديم (string)
        uploadedImageUrl = formData.imageUrl
          ? `https://api.generalhome.sa${formData.imageUrl}`
          : "";
      }

      // جهز الباي لود
      const payloadPost = {
        Id: 0,
        text: formData.title,
        image: uploadedImageUrl,
      };

      const payloadPut = {
        Id: formData.id,
        text: formData.title,
        image: uploadedImageUrl,
      };

      if (formData.id) {
        // If editing, send a PUT request
        await axios.put(
          `https://api.generalhome.sa/api/Themes/PutTeam/${formData.id}`,
          payloadPut,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        // If adding, send a POST request
        await axios.post(
          "https://api.generalhome.sa/api/Themes/PostTeam",
          payloadPost,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      // Show success message using SweetAlert
      Swal.fire({
        icon: "success",
        title: "تم!",
        text: formData.id
          ? "تم تحديث البيانات بنجاح!"
          : "تم إرسال البيانات بنجاح!",
        confirmButtonText: "حسناً",
      });

      // Reset form
      setFormData({
        id: null,
        title: "",
        imageUrl: "",
      });

      // Reset file input manually
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refetch the teams data to update the table
      const fetchResponse = await axios.get(
        "https://api.generalhome.sa/api/Themes/GetTeams"
      );
      setTeams(fetchResponse.data);
    } catch (error) {
      console.error("Error submitting data:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ!",
        text: "حدث خطأ أثناء إرسال البيانات!",
        confirmButtonText: "حسناً",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Edit button click
  const handleEdit = (team) => {
    setFormData({
      id: team.Id,
      title: team.text,
      imageUrl: team.image.replace("https://api.generalhome.sa/", ""),
    });
  };

  const handleDelete = async (Id) => {
    const result = await Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لن تتمكن من استعادة هذه البيانات!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم، احذفها",
      cancelButtonText: "إلغاء",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    // If the user confirms the deletion
    if (result.isConfirmed) {
      try {
        await axios.delete(
          `https://api.generalhome.sa/api/Themes/DeleteTeam/${Id}`
        );

        // Show success message using SweetAlert
        Swal.fire({
          icon: "success",
          title: "تم!",
          text: "تم حذف البيانات بنجاح!",
          confirmButtonText: "حسناً",
        });

        // Refetch the teams data to update the table
        const fetchResponse = await axios.get(
          "https://api.generalhome.sa/api/Themes/GetTeams"
        );
        setTeams(fetchResponse.data);
      } catch (error) {
        console.error("Error deleting team:", error);

        // Show error message using SweetAlert
        Swal.fire({
          icon: "error",
          title: "خطأ!",
          text: "حدث خطأ أثناء حذف البيانات!",
          confirmButtonText: "حسناً",
        });
      }
    }
  };

  const normalizeImageUrl = (url) => {
    if (!url || typeof url !== "string") return null;

    try {
      // Extract path only (remove wrong domain if exists)
      const parsed = new URL(url);
      const pathname = parsed.pathname;

      // Validate image extension
      if (!/\.(jpg|jpeg|png|webp|gif)$/i.test(pathname)) {
        return null;
      }

      // Always force correct base domain
      return `https://api.generalhome.sa${pathname}`;
    } catch {
      return null;
    }
  };

  return (
    <>
      <MobileNav />
      <section className="teamControl">
        <div className="teamContrl-container container">
          <form onSubmit={handleSubmit}>
            {/* Title Input */}
            <div className="form-group">
              <label htmlFor="title">اسم الفني:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="أدخل اسم الفني"
              />
              {errors.title && <p className="error">{errors.title}</p>}
            </div>

            {/* Image URL Input */}
            <div className="form-group">
              <label htmlFor="imageUrl">رابط الصورة:</label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={(e) =>
                  setFormData({ ...formData, imageUrl: e.target.files[0] })
                }
              />

              {errors.imageUrl && <p className="error">{errors.imageUrl}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {formData.id
                ? isLoading
                  ? "جاري التحديث..."
                  : "تحديث التغييرات"
                : isLoading
                ? "جاري الحفظ..."
                : "حفظ التغييرات"}
            </button>
          </form>

          {/* Team Members Table */}
          <div className="team-members-table">
            <h2>أعضاء الفريق</h2>
            {teams.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>اسم الفني</th>
                    <th>الصورة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map((team) => (
                    <tr key={team.Id}>
                      <td>{team.text}</td>
                      <td>
                        {normalizeImageUrl(team.image) && (
                          <div className="image-box">
                            <img
                              src={normalizeImageUrl(team.image)}
                              alt={team.text || "صورة الفريق"}
                              loading="lazy"
                              style={{ width: "50px", height: "50px" }}
                            />
                          </div>
                        )}
                      </td>

                      <td>
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(team)}
                        >
                          تعديل
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(team.Id)}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>لا يوجد أعضاء فريق مسجلين</p>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default TeamControl;
